import { CommonModule, Location } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { userRoles } from '../../../../environments/environment';
import { ButtonComponent } from '../../../shared/components/custom-button/button.component';
import { TableComponent } from '../../../shared/components/custom-table/table.component';
import { AddAudienceComponent } from '../../../shared/components/dialog/add-audience/add-audience.component';
import { AlertDialogComponent, AlertDialogData } from '../../../shared/components/dialog/alert-dialog/alert-dialog.component';
import { CommonDialogComponent } from '../../../shared/components/dialog/commom/commom-dialog.component';
import { CustomToastComponent } from '../../../shared/components/toast/custom-toast.component';
import { HttpService } from '../../../shared/services/http.service';

interface EventType {
    audienceTypeId: string;
    id: string;
    audienceTypeName: string;
    audienceDescription: string;
    status: boolean;
    pointsAwarded: number;
    eventTypeId: string;
    receivedAttendingPoints: number;
}

interface APIResponse {
    status: boolean;
    message: string;
    data: EventType | [];
}

@Component({
    selector: 'app-audience',
    standalone: true,
    imports: [
        CommonModule,
        TableComponent,
        ButtonComponent,
        MatDialogModule,
        AddAudienceComponent,
        FormsModule
    ],
    templateUrl: './audience.component.html',
    styleUrls: ['./audience.component.scss'],
})
export class AudienceComponent implements OnInit {
    @ViewChild('actionButtons') actionButtonsTemplate!: TemplateRef<any>;
    @ViewChild('addEventTypeContent') addEventTypeContent!: TemplateRef<any>;
    @ViewChild(TableComponent) tableComponent!: TableComponent;

    loading = false;
    submitLoading = false;
    data: any[] = [];
    editingEventType: EventType | null = null;
    columns: string[] = ['S.No', 'audience name', 'description', 'status'];
    fullData: any[] = [];
    search: string = '';
    userRoles = {
        addNew: false,
        genarateReport: false,
        listItem: {
            edit: false,
            statusChange: false
        }
    }

    constructor(
        private dialog: MatDialog,
        private httpService: HttpService,
        private snackBar: MatSnackBar,
        private cdr: ChangeDetectorRef,
        private location: Location,
        private router: Router,

    ) { }

    ngOnInit(): void {
        this.fetchAudience();

        const user = localStorage.getItem('userRole');
        if (user && user in userRoles) {
            this.userRoles = userRoles[user as keyof typeof userRoles]?.audiance
        }
    }

    fetchAudience(): void {
        this.loading = true;

        this.httpService.get<APIResponse>('audiencetypes?status=2').subscribe({
            next: (response) => {
                const dataArray = Array.isArray(response.data) ? response.data : response.data ? [response.data] : [];
                const previousSearchTerm = this.search;
                this.fullData = dataArray.map((item, idx) => ({
                    id: item.audienceTypeId,
                    "S.No": idx + 1,
                    'audience name': item.audienceTypeName || '',
                    status: item.status || 'Inactive',
                    description: item.audienceDescription || '',
                    isStatusClickable: true,
                }));
                if (previousSearchTerm && previousSearchTerm.trim() !== '') {
                    this.search = previousSearchTerm;
                    this.applyFilter();
                } else {
                    this.data = [...this.fullData];
                }
                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Failed to load Event Types:', err);
                this.showErrorToast(err?.message);
                this.loading = false;
            },
            complete: () => {
                this.loading = false;
            }
        });
    }

    navigateReports(): void {
        this.router.navigate(['/reports/report-dashboard']);
    }

    updateSearch(searchValue: string): void {
        this.search = searchValue;
        this.applyFilter();
    }

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }
    applyFilter(): void {
        if (!this.search || this.search.trim() === '') {
            this.data = [...this.fullData];
            return;
        }

        // Match only the columns on screen: the rows also carry ids, GUIDs and internal flags,
        // and searching those makes rows with no visible match appear. Descriptions can hold
        // newlines or doubled spaces that render as a single space, so collapse whitespace on
        // both sides - otherwise text copied off the screen can never match what is stored.
        const normalize = (value: unknown): string =>
            (value ?? '')
                .toString()
                .toLowerCase()
                .replace(/\s+/g, ' ')
                .trim();

        const searchTerm = normalize(this.search);
        this.data = this.fullData.filter(item =>
            this.columns.some(column => normalize(item[column]).includes(searchTerm))
        );

        this.resetPagination();
        this.cdr.detectChanges();
    }

    private resetPagination(): void {
        this.data = [...this.data];
        if (this.tableComponent) {
            this.tableComponent.currentPage = 1;
            this.tableComponent.cdr?.detectChanges();
        }
    }

    onEventTypeFormSubmit(formData: any): void {
        if (this.submitLoading) return
        this.submitLoading = true;
        const dialogRefs = this.dialog.openDialogs;

        const currentDialogRef = dialogRefs[dialogRefs.length - 1];

        if (currentDialogRef && currentDialogRef.componentInstance instanceof CommonDialogComponent) {
            if (this.editingEventType) {
                currentDialogRef.componentInstance.data.confirmText = 'Updating...';
            } else {
                currentDialogRef.componentInstance.data.confirmText = 'Submitting...';
            }
            currentDialogRef.componentInstance.data.loading = true;
            currentDialogRef.componentInstance.cdr.detectChanges();
        }
        const payload = {
            audienceTypeName: formData.audienceTypeName,
            audienceDescription: formData.audienceDescription,
            status: formData.status
        };

        this.submitLoading = true;
        const apiCall = this.editingEventType
            ? this.httpService.put<APIResponse>(`audiencetypes/${this.editingEventType.id}`, payload)
            : this.httpService.post<APIResponse>('audiencetypes', payload);

        apiCall.subscribe({
            next: (response) => {
                if (response.status) {
                    this.showSuccessToast(response.message);
                    this.dialog.closeAll();
                    this.fetchAudience();
                    this.editingEventType = null;
                } else {
                    this.showErrorToast(response.message);
                    if (currentDialogRef && currentDialogRef.componentInstance instanceof CommonDialogComponent) {
                        currentDialogRef.componentInstance.data.confirmText = this.editingEventType ? 'Update' : 'Submit';
                        currentDialogRef.componentInstance.data.loading = false;
                        currentDialogRef.componentInstance.cdr.detectChanges();
                    }
                    this.submitLoading = false;
                    return;
                }
                this.submitLoading = false;
            },
            error: (err) => {
                console.error('Failed to save Event Type:', err);
                this.showErrorToast(err.message);
                if (currentDialogRef && currentDialogRef.componentInstance instanceof CommonDialogComponent) {
                    currentDialogRef.componentInstance.data.confirmText = this.editingEventType ? 'Update' : 'Submit';
                    currentDialogRef.componentInstance.data.loading = false;
                    currentDialogRef.componentInstance.cdr.detectChanges();
                }
                this.submitLoading = false;
            },
            complete: () => {
                this.submitLoading = false;
            }
        });
    }

    openAddDialog(eventTypeToEdit: EventType | null = null): void {
        this.editingEventType = eventTypeToEdit;
        const initialData = eventTypeToEdit ? {
            ...eventTypeToEdit,
            status: eventTypeToEdit.status
        } : null;

        const dialogRef = this.dialog.open(CommonDialogComponent, {
            data: {
                title: eventTypeToEdit ? 'Edit audience' : 'Add new audience',
                confirmText: eventTypeToEdit ? 'Update' : 'Submit',
                cancelText: 'Cancel',
                template: this.addEventTypeContent,
                initialData: initialData,
                loading: false
            },
            width: '50.5rem',
            maxWidth: '100vw',
            disableClose: true,
            autoFocus: false
        });

        dialogRef.afterClosed().subscribe(() => {
            this.editingEventType = null;
        });
    }

    onEdit(eventType: EventType): void {
        this.openAddDialog(eventType);
    }

    onDelete(user: any): void {
        const dialogData: AlertDialogData = {
            title: 'Confirm delete',
            message: `Are you sure you want to delete ${user.audienceName}?`,
            okText: 'Delete',
            cancelText: 'Cancel',
            icon: 'warning',
            submessage: "",
        };

        const dialogRef = this.dialog.open(AlertDialogComponent, {
            width: '25.438rem',
            data: dialogData,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.httpService.delete<APIResponse>(`audiencetypes/${user.id}`).subscribe({
                    next: response => {
                        if (response?.status) {
                            this.fullData = this.fullData.filter(u => u.id !== user.id);
                            this.data = this.data.filter(u => u.id !== user.id);
                            this.adjustPaginationAfterDelete();
                            this.showSuccessToast(response.message);
                        } else {
                            this.showErrorToast(response.message);
                        }
                    },
                    error: (err) => {
                        this.showErrorToast(err?.message);
                    },
                });
            }
        });
    }

    onStatusClick(event: { row: any; id: any }): void {
        if (!this.userRoles.listItem.statusChange) return
        const { row, id } = event;
        const currentStatus = row.status;
        const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';

        const dialogData: AlertDialogData = {
            title: 'Status update',
            message: `Are you sure you want to ${newStatus === 'Active' ? 'Activate' : 'Deactivate'} ?`,
            okText: 'Update',
            cancelText: 'Cancel',
            icon: 'warning',
            submessage: ""
        };

        const dialogRef = this.dialog.open(AlertDialogComponent, {
            width: '25.438rem',
            data: dialogData,
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                const apiStatus = newStatus === 'Active';

                this.httpService.put<APIResponse>(`audiencetypes/${id}/status`, { status: apiStatus }).subscribe({
                    next: response => {
                        if (response?.status) {
                            this.updateLocalAudienceStatus(id, newStatus);
                            this.showSuccessToast(response.message);
                        } else {
                            this.showErrorToast(response.message);
                        }
                    },
                    error: () => {
                        this.showErrorToast('An error occurred while updating the audience status.');
                    },
                });
            }
        });
    }

    private updateLocalAudienceStatus(audienceId: string, newStatus: string): void {
        this.fullData = this.fullData.map(item => {
            if (item.id === audienceId) {
                return {
                    ...item,
                    status: newStatus
                };
            }
            return item;
        });

        this.data = this.data.map(item => {
            if (item.id === audienceId) {
                return {
                    ...item,
                    status: newStatus
                };
            }
            return item;
        });

        this.cdr.detectChanges();
    }

    goBack() {
        this.location.back();
    }

    private adjustPaginationAfterDelete(): void {
        if (!this.tableComponent) return;

        const totalPages = Math.ceil(this.data.length / this.tableComponent.pageSize);
        if (this.tableComponent.currentPage > totalPages && totalPages > 0) {
            this.tableComponent.currentPage = totalPages;
        }
    }

    private showSuccessToast(message: string): void {
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'success-toast']
        });
    }

    private showErrorToast(message: string | null | undefined): void {
        if (!message) return;
        this.snackBar.openFromComponent(CustomToastComponent, {
            data: { message },
            duration: 3000,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: ['no-padding-snackbar', 'error-toast']
        });
    }
}